import {
  ITBox,
  QboAlert,
  QboFormGroup,
  QboModal,
  QboSimpleDataTable,
  QboTextField,
  QboTypography,
} from '@ui/Components';
import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import ErrorIcon from '@mui/icons-material/Error';
import { FormControl, Grid, MenuItem, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import PartnerFilterSection from '@ui/Sections/Shared/PartnerFilterSection';
import useAction from './pageHooks/useAction';

function FormModal({ closeModal, showModal, title, children }) {
  const [errorMessage, setErrorMessage] = useState();

  const { form, isLoading, handleOnValidateData, resetAll, disabled } = useAction({
    onSuccessRequest: closeModal,
    onFailedRequest: setErrorMessage,
  });
  const { t } = useTranslation();

  const closeAndReset = () => {
    setErrorMessage(null);
    closeModal();
    resetAll();
  };

  return (
    <QboModal
      onClose={closeAndReset}
      open={showModal}
      title={title}
      showCloseIcon
      preventOutsideClick>
      <ITBox direction="TransferSubscriptionFormPage__wrapper column">
        <QboFormGroup
          loadingButton={isLoading}
          buttonDisabled={disabled}
          onSubmit={(e) => {
            setErrorMessage(null);
            handleOnValidateData(e);
          }}
          withGenerateAndResetButton
          handleOnResetButton={closeAndReset}
          secondButtonLabel="Cancel"
          firstButtonLabel="Submit">
          {errorMessage && (
            <QboAlert
              variant="outlined"
              type="error"
              style={{
                background: '#fdeded',
                fontSize: '10px !important',
              }}>
              <p>
                {t('partner_list_page.bulk_create_organization.error', { error: errorMessage })}
              </p>
            </QboAlert>
          )}
          {children}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <QboTextField
                id="supportTicket"
                placeholder="Support Ticket"
                label="Support Ticket"
                fullWidth
                value={form.supportTicketAttributes.value}
                errorMessage={form.supportTicketAttributes.errorMessage}
                inputProps={{ maxLength: 225 }}
                onChange={(e) => {
                  form.supportTicketAttributes.handleChange(e.currentTarget.value);
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <QboTextField
                id="escalationTicket"
                placeholder="Escalation Ticket"
                label="Escalation Ticket"
                fullWidth
                value={form.escalationTicketAttributes.value}
                onChange={(e) =>
                  form.escalationTicketAttributes.handleChange(e.currentTarget.value)
                }
                inputProps={{ maxLength: 225 }}
              />
            </Grid>
          </Grid>
          <QboTextField
            id="description"
            placeholder="Description"
            label="Description"
            fullWidth
            value={form.descriptionAttributes.value}
            onChange={(e) => form.descriptionAttributes.handleChange(e.currentTarget.value)}
            inputProps={{ maxLength: 225 }}
          />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Period"
                  fullWidth
                  views={['year', 'month']}
                  defaultValue={dayjs()}
                  maxDate={dayjs()}
                  minDate={dayjs().subtract(5, 'year')}
                  onChange={(period) => {
                    form.periodAttributes.handleChange(period);
                  }}
                  sx={{ width: '100%', marginTop: 2.5 }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={6}>
              <FormControl className="FormControl__default-wrapper FormControl__full-width-wrapper">
                <div className="QboTextField__label" id="transferType">
                  Reseller
                  <span style={{ color: 'red' }}> *</span>
                </div>
                <PartnerFilterSection
                  label=""
                  value={form.resellerIdAttributes.value}
                  marginRightValue={2}
                  buttonWidth="100%"
                  inputSize={null}
                  defaultValue={null}
                  handleChange={(value) => {
                    form.resellerIdAttributes.handleChange(value);
                  }}
                />
                {form.resellerIdAttributes.errorMessage && (
                  <span className="QboTextField__error-text">
                    <ErrorIcon />
                    {` ${form.resellerIdAttributes.errorMessage}`}
                  </span>
                )}
              </FormControl>
            </Grid>
          </Grid>
          <QboTextField
            id="Seat Payload"
            label="Seat Payload"
            rows={10}
            required
            fullWidth
            multiline={Boolean(true)}
            value={form.seatPayloadAttributes.value}
            onChange={(e) => {
              form.seatPayloadAttributes.handleChange(e.currentTarget.value);
            }}
            errorMessage={form.seatPayloadAttributes.errorMessage}
          />
        </QboFormGroup>
      </ITBox>
    </QboModal>
  );
}

FormModal.propTypes = {
  closeModal: PropTypes.func,
  showModal: PropTypes.bool,
  title: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
};

FormModal.defaultProps = {
  closeModal: () => {},
  showModal: false,
  title: '',
  children: '',
};

export default FormModal;
